<template>
    <v-modal title="Edit Customer" @enter="enter" @close="close" size="large" scroll>
        <div slot="content">
            <v-error-messages :has-frontend-errors="$v.localContact.$error" class="stack-l">
                <li class="list-item" v-if="!$v.localContact.hasContactMethod">Must provide at least one contact method (email or phone number)</li>
            </v-error-messages>
            <div class="grid-content" v-fs-block>
                <div class="grid-item gird-item-full">
                    <p class="stack-m">Edit your customer information.</p>
                </div>
                <div class="grid-item grid-item-full">
                    <r-input
                        label="First Name"
                        :auto-focus="true"
                        v-model="localContact.firstName"
                    />
                </div>
                <div class="grid-item grid-item-full">
                    <r-input
                        label="Last Name"
                        v-model="localContact.lastName"
                    />
                </div>
                <div class="grid-item grid-item-full">
                    <r-input
                        label="Organization"
                        v-model="localContact.organization"
                    />
                </div>
                <div class="grid-item grid-item-full">
                    <v-select
                        label="Website Id"
                        :options="websiteIds"
                        v-model="localCustomer.websiteId"
                        placeholder="Select a website"
                    />
                </div>
                <v-custom-fields-manager
                    fluid
                    resource-type="customers"
                    v-model="localCustomer.customFields"/>
                <div class="grid-item grid-item-full">
                    <r-input
                        label="Address Line 1"
                        help-text="Street address, P.O. box, company name, c/o"
                        v-model="localContact.address"
                    />
                </div>
                <div class="grid-item grid-item-full">
                    <r-input
                        label="Address Line 2"
                        help-text="Apartment, suite, unit, building, floor, etc"
                        v-model="localContact.address2"
                    />
                </div>
                <div class="grid-item grid-item-full">
                    <r-input
                        label="City"
                        v-model="localContact.city"
                    />
                </div>
                <div class="grid-item grid-item-full">
                    <v-select
                        label="Country"
                        :options="countryList"
                        v-model="localContact.country"
                    />
                </div>
                <div class="grid-item grid-item-full">
                    <v-select
                        v-if="hasRegion"
                        label="Region (state or province)"
                        :options="regionNames"
                        option-label="regionName"
                        option-key="regionCode"
                        v-model="localContact.region"
                    />
                    <r-input
                        v-else
                        label="Region (state or province)"
                        v-model="localContact.region"
                    />
                </div>
                <div class="grid-item grid-item-full">
                    <r-input
                        label="Postal/Zip Code"
                        v-model="localContact.postalCode"
                    />
                </div>
                <div class="grid-item grid-item-full">
                    <contact-phones-emails v-model="localContact" ref="contact-methods" :hasError="$v.localContact.$error"/>
                </div>
            </div>
        </div>
        <template slot="actions">
            <r-button type="primary" @click="save" :loading="isLoading">Save</r-button>
        </template>
    </v-modal>
</template>

<script>
    import {mapState, mapActions} from 'vuex';
    import vModal from '@/components/modal/v-modal';
    import {
        vErrorMessages, vSelect,
    } from '@/components/form';
    import vCustomFieldsManager from '@/components/custom-fields/v-custom-fields-manager';
    import ContactForm from '@/modules/customers/components/contact-form';
    import ContactPhonesEmails from '@/components/contact-manager/contact-phones-emails';
    import {COUNTRIES_LIST} from '@/data/countries';
    import REGIONS from '@/data/regions';

    export default {
        components: {
            vModal,
            vErrorMessages,
            vSelect,
            vCustomFieldsManager,
            ContactForm,
            ContactPhonesEmails,
        },
        data() {
            return {
                isLoading: false,
                websiteIds: [],
                countryList: COUNTRIES_LIST,
                regionList: {...REGIONS},
                localContact: null,
                localCustomer: null,
            };
        },
        computed: {
            ...mapState('customers', ['customer']),
            regionNames() {
                return this.regionList[this.localContact.country];
            },
            hasRegion() {
                return ['CA', 'US'].includes(this.localContact.country);
            },
        },
        methods: {
            ...mapActions({
                updateCustomer: 'customers/updateCustomer',
            }),
            async save() {
                this.isLoading = true;
                this.$v.$touch();
                const isValid = this.$refs['contact-methods'].validate() && !this.$v.$error;
                if (isValid) {
                    try {
                        await this.updateCustomer({
                            data: {
                                customFields: {
                                    ...this.localCustomer.customFields,
                                },
                                primaryAddress: {
                                    ...this.localContact,
                                },
                            },
                        });
                        this.isLoading = false;
                        this.close();
                    } catch (error) {
                        this.$toast.fromError(error);
                    }
                } else {
                    this.isLoading = false;
                }
            },
            enter() {
                this.localContact = this.customer.primaryAddress.cloneSelf();
            },
            close() {
                this.$emit('close');
            },
            clearRegion() {
                this.localContact.region = null;
            },
            async fetchData() {
                this.isLoading = true;
                try {
                    const {items: websiteItems} = await this.$api(({websites}) => websites.getAll());
                    this.websiteIds = websiteItems.map(({fields: {id: websiteID}}) => websiteID);
                } catch (error) {
                    this.$toast.fromError(error, 'Can not retrieve websites, please try again or contact support.');
                } finally {
                    this.isLoading = false;
                }
            },
        },
        validations: {
            localContact: {
                hasContactMethod: value => value.emails.length > 0 || value.phoneNumbers.length > 0,
            },
        },
        created() {
            this.localContact = this.customer.primaryAddress.cloneSelf();
            this.localCustomer = this.customer.cloneSelf({...this.customer});
            this.fetchData();
        },
    };
</script>
